import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import employeeCodeSettingsService from '../../../services/employeeCodeSettingsService';
import NotificationToast from '../../../components/ui/NotificationToast';

const EmployeeCodeSettings = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        prefix: 'EMP-',
        last_number: 0
    });
    const [formData, setFormData] = useState({
        prefix: 'EMP-',
        last_number: 0
    });
    const [previewCode, setPreviewCode] = useState('');
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        loadSettings();
    }, []);

    useEffect(() => {
        if (formData.prefix && formData.last_number !== null) {
            const nextNumber = parseInt(formData.last_number) + 1;
            const preview = formData.prefix + String(nextNumber).padStart(4, '0');
            setPreviewCode(preview);
        }
    }, [formData]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const result = await employeeCodeSettingsService.getSettings();
            if (result?.success) {
                setSettings(result.data);
                setFormData({
                    prefix: result.data.prefix,
                    last_number: result.data.last_number
                });
            } else {
                setNotification({ message: result?.error || 'Failed to load settings', type: 'error' });
            }
        } catch (error) {
            setNotification({ message: 'An error occurred', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        // Validation
        if (!formData.prefix.trim()) {
            setNotification({ message: 'Prefix cannot be empty', type: 'error' });
            return;
        }

        if (!/^[A-Z0-9\-]+$/i.test(formData.prefix)) {
            setNotification({ message: 'Prefix can only contain letters, numbers, and hyphens', type: 'error' });
            return;
        }

        if (parseInt(formData.last_number) < parseInt(settings.last_number)) {
            setNotification({
                message: `Cannot set starting number lower than current value (${settings.last_number}) to prevent duplicate codes`,
                type: 'error'
            });
            return;
        }

        try {
            setSaving(true);
            const result = await employeeCodeSettingsService.updateSettings({
                prefix: formData.prefix.toUpperCase(),
                last_number: parseInt(formData.last_number)
            });

            if (result?.success) {
                setNotification({ message: 'Settings saved successfully', type: 'success' });
                setSettings(result.data);
                setFormData({
                    prefix: result.data.prefix,
                    last_number: result.data.last_number
                });
            } else {
                setNotification({ message: result?.error || 'Failed to save settings', type: 'error' });
            }
        } catch (error) {
            setNotification({ message: 'An error occurred', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setFormData({
            prefix: settings.prefix,
            last_number: settings.last_number
        });
    };

    const hasChanges = formData.prefix !== settings.prefix ||
        parseInt(formData.last_number) !== parseInt(settings.last_number);

    if (loading) {
        return (
            <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading settings...</p>
            </div>
        );
    }

    return (
        <div className="p-6">
            <NotificationToast
                message={notification?.message}
                type={notification?.type}
                onDismiss={() => setNotification(null)}
            />

            <div className="mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">Employee Code Configuration</h3>
                <p className="text-sm text-muted-foreground">
                    Configure the prefix and starting number for auto-generated employee codes
                </p>
            </div>

            <div className="space-y-6 max-w-2xl">
                {/* Prefix Configuration */}
                <div>
                    <Input
                        label="Employee Code Prefix"
                        value={formData.prefix}
                        onChange={(e) => handleInputChange('prefix', e.target.value)}
                        placeholder="EMP-"
                        maxLength={10}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        Prefix for employee codes (letters, numbers, and hyphens only). Max 10 characters.
                    </p>
                </div>

                {/* Current Sequence Number */}
                <div>
                    <Input
                        label="Current Sequence Number"
                        type="number"
                        value={formData.last_number}
                        onChange={(e) => handleInputChange('last_number', e.target.value)}
                        min={settings.last_number}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        The last used number. Next employee will get number {parseInt(formData.last_number) + 1}.
                        Cannot be set lower than {settings.last_number} to prevent duplicates.
                    </p>
                </div>

                {/* Preview */}
                <div className="bg-muted/50 border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <Icon name="Eye" size={20} className="text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium text-foreground mb-1">Next Employee Code Preview</p>
                            <p className="text-2xl font-mono font-bold text-primary">{previewCode}</p>
                        </div>
                    </div>
                </div>

                {/* Warning */}
                {hasChanges && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex gap-3">
                            <Icon name="AlertTriangle" size={20} className="text-yellow-600 dark:text-yellow-500 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                                    Unsaved Changes
                                </p>
                                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                    Changes to these settings will affect all new employees created after saving.
                                    {formData.prefix !== settings.prefix && ' Changing the prefix will not affect existing employee codes.'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-border">
                    <Button
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        iconName={saving ? "Loader" : "Save"}
                        iconPosition="left"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleReset}
                        disabled={!hasChanges || saving}
                    >
                        Reset
                    </Button>
                </div>

                {/* Info */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex gap-3">
                        <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                            <p><strong>How it works:</strong></p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Each new employee gets a unique code combining the prefix and an auto-incrementing number</li>
                                <li>Employee codes are immutable and cannot be changed after creation</li>
                                <li>The sequence number automatically increments with each new employee</li>
                                <li>Format: Prefix + 4-digit number (e.g., EMP-0001, EMP-0002)</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeCodeSettings;
